import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, NgZone, Output, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import type {PDFDocumentProxy, PDFPageProxy} from 'pdfjs-dist/types/src/display/api';
import type {PageViewport} from 'pdfjs-dist/types/src/display/display_utils';
import {EMPTY, from, fromEvent, Subject} from 'rxjs';
import {catchError, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {SignatureInfo, SigningFieldPlacement} from '../../files/files.model';
import {ModalService} from '../../modal';
import {EmployeeDocument} from '../documents.interface';
import {DocumentsService} from '../documents.service';

interface SignatureField {
  id: number;
  role: string;
  placement: SigningFieldPlacement;
}

interface FieldDrag {
  id: number;
  leftOffset: number;
  topOffset: number;
}

const MODAL_ID = 'document-signature-fields';

@Component({
  selector: 'gp-signature-fields',
  templateUrl: './signature-fields.component.html',
  styleUrls: ['./signature-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignatureFieldsComponent extends Destroyable {
  @Output() public sendForSignature = new EventEmitter<EmployeeDocument>();
  @ViewChild('signaturePlacementPreview') public signaturePlacementPreview: ElementRef<HTMLElement>;

  public roleControl = new UntypedFormControl('');
  public document: EmployeeDocument;
  public roles: string[] = [];
  public fields: SignatureField[] = [];
  public source: Uint8Array;
  public visible = false;
  public loading = false;
  public saving = false;
  public loadFailed = false;
  public blocked = false;
  public pageNumber = 1;
  public pageCount = 0;
  public zoom = 0.8;
  public placingRole: string = null;
  public pendingDeletionRole: string = null;
  public selectedFieldId: number = null;
  public viewport: PageViewport;

  private pageView: number[];
  private nextFieldId = 0;
  private drag: FieldDrag;
  private close$ = new Subject<void>();

  constructor(private documentsService: DocumentsService,
              private modalService: ModalService,
              private translate: TranslateService,
              private toastrService: ToastrService,
              private ngZone: NgZone,
              private cd: ChangeDetectorRef) {
    super();
    this.modalService.modalClosed.pipe(
      tap((id) => {
        if (id === MODAL_ID) {
          this.reset();
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public get canAddRole(): boolean {
    const role = (this.roleControl.value ?? '').trim();
    return !!role && !this.roles.some((existing) => existing.toLocaleLowerCase() === role.toLocaleLowerCase());
  }

  public get canSave(): boolean {
    return !this.loading && !this.saving && !this.loadFailed && !this.blocked && !!this.viewport
      && this.roles.every((role) => this.countForRole(role) > 0);
  }

  public get pageFields(): SignatureField[] {
    return this.fields.filter((field) => field.placement.page === this.pageNumber);
  }

  public open(document: EmployeeDocument): void {
    this.reset();
    this.document = document;
    const infos = document.signatureData?.signatureInfos ?? [];
    this.roles = [...new Set(infos.map((info) => info.markerName).filter((role) => !!role))];
    this.fields = infos.filter((info) => info.markerName && info.signingFieldPlacement)
      .map((info) => ({id: ++this.nextFieldId, role: info.markerName, placement: {...info.signingFieldPlacement}}));
    this.visible = true;
    this.modalService.openModalWithId(MODAL_ID);
    this.ngZone.runOutsideAngular(() => {
      fromEvent<PointerEvent>(window.document, 'pointermove').pipe(
        tap((event) => this.moveSignaturePlacementPreview(event)),
        takeUntil(this.close$),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
    });
    this.loadDocument();
  }

  public loadDocument(): void {
    this.loading = true;
    this.loadFailed = false;
    this.documentsService.getDocument(this.document.id).pipe(
      switchMap((file) => from(file.blob.arrayBuffer())),
      tap((buffer) => {
        this.source = new Uint8Array(buffer);
        this.cd.markForCheck();
      }),
      catchError(() => {
        this.onPdfError();
        return EMPTY;
      }),
      takeUntil(this.close$),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public onPdfLoaded(pdf: PDFDocumentProxy): void {
    this.pageCount = pdf.numPages;
    this.cd.markForCheck();
  }

  public onPageRendered(event: CustomEvent): void {
    const rendered = event as unknown as {source: {viewport: PageViewport; pdfPage: PDFPageProxy}; error?: unknown};
    if (!this.visible || rendered.source.pdfPage.pageNumber !== this.pageNumber) {
      return;
    }
    if (rendered.error) {
      this.onPdfError();
      return;
    }
    this.viewport = rendered.source.viewport;
    this.pageView = rendered.source.pdfPage.view;
    this.loading = false;
    this.cd.markForCheck();
  }

  public onPdfError(): void {
    this.loadFailed = true;
    this.loading = false;
    this.cd.markForCheck();
  }

  public addRole(): void {
    if (!this.canAddRole || this.saving || this.blocked) {
      return;
    }
    const role = this.roleControl.value.trim();
    this.roles = [...this.roles, role];
    this.roleControl.reset('');
    this.placingRole = role;
  }

  public countForRole(role: string): number {
    return this.fields.filter((field) => field.role === role).length;
  }

  public roleColor(role: string): string {
    return `signature-color-${this.roles.indexOf(role) % 6}`;
  }

  public moveSignaturePlacementPreview(event: PointerEvent): void {
    const preview = this.signaturePlacementPreview?.nativeElement;
    if (!this.placingRole || !preview || event.pointerType === 'touch') {
      return;
    }
    const origin = preview.offsetParent?.getBoundingClientRect();
    preview.style.transform = `translate3d(${event.clientX - (origin?.left ?? 0) + 16}px, ${event.clientY - (origin?.top ?? 0) + 16}px, 0)`;
    preview.classList.add('signature-placement-preview--visible');
  }

  @HostListener('document:keydown.escape')
  public cancelSignaturePlacement(): void {
    if (!this.placingRole) {
      return;
    }
    this.placingRole = null;
    this.cd.markForCheck();
  }

  @HostListener('document:pointerdown', ['$event'])
  public cancelSignaturePlacementOutsideDocument(event: PointerEvent): void {
    if (this.placingRole && event.target instanceof Element
      && !event.target.closest('.signature-overlay, [test-id="placeSignature"], [test-id="addSignatureRole"]')) {
      this.cancelSignaturePlacement();
    }
  }

  public deleteRole(role: string): void {
    if (this.saving || this.blocked) {
      return;
    }
    this.fields = this.fields.filter((field) => field.role !== role);
    this.roles = this.roles.filter((existing) => existing !== role);
    this.pendingDeletionRole = null;
    this.placingRole = null;
    this.selectedFieldId = null;
  }

  public deleteField(field: SignatureField): void {
    this.fields = this.fields.filter((existing) => existing.id !== field.id);
    this.selectedFieldId = null;
  }

  public changePage(page: number): void {
    if (page < 1 || page > this.pageCount || this.loading) {
      return;
    }
    this.pageNumber = page;
    this.viewport = null;
    this.loading = true;
    this.selectedFieldId = null;
    this.drag = null;
  }

  public changeZoom(delta: number): void {
    this.zoom = Math.round(Math.max(0.4, Math.min(1.6, this.zoom + delta)) * 10) / 10;
    this.viewport = null;
    this.loading = true;
    this.drag = null;
  }

  public fieldBounds(field: SignatureField): {left: number; top: number; width: number; height: number} {
    const placement = field.placement;
    const rectangle = this.viewport.convertToViewportRectangle([
      this.pageView[0] + placement.left,
      this.pageView[3] - placement.top - placement.height,
      this.pageView[0] + placement.left + placement.width,
      this.pageView[3] - placement.top
    ]);
    return {
      left: Math.min(rectangle[0], rectangle[2]),
      top: Math.min(rectangle[1], rectangle[3]),
      width: Math.abs(rectangle[2] - rectangle[0]),
      height: Math.abs(rectangle[3] - rectangle[1])
    };
  }

  public fieldFontSize(field: SignatureField): number {
    const bounds = this.fieldBounds(field);
    return Math.min(12 * this.viewport.scale, bounds.width / (field.role.length * 0.6 + 6), bounds.height / 2);
  }

  public placeField(event: PointerEvent): void {
    if (!this.placingRole || !this.viewport || this.loading || this.saving || this.blocked || event.button !== 0) {
      return;
    }
    const point = this.pdfPoint(event);
    const width = Math.min(150, this.pageView[2] - this.pageView[0]);
    const height = Math.min(45, this.pageView[3] - this.pageView[1]);
    const field: SignatureField = {
      id: ++this.nextFieldId,
      role: this.placingRole,
      placement: {page: this.pageNumber, left: point.left - width / 2, top: point.top - height / 2, width, height}
    };
    this.clampField(field);
    this.fields = [...this.fields, field];
    this.selectedFieldId = field.id;
    this.placingRole = null;
  }

  public startDrag(event: PointerEvent, field: SignatureField): void {
    event.stopPropagation();
    if (this.saving || this.blocked || event.button !== 0 || !this.viewport) {
      return;
    }
    event.preventDefault();
    this.placingRole = null;
    this.selectedFieldId = field.id;
    const target = event.currentTarget as HTMLElement;
    const overlay = target.parentElement;
    const point = this.pdfPoint(event, overlay);
    this.drag = {id: field.id, leftOffset: point.left - field.placement.left, topOffset: point.top - field.placement.top};
    target.setPointerCapture(event.pointerId);
    target.focus();
  }

  public moveField(event: PointerEvent, field: SignatureField): void {
    if (this.drag?.id !== field.id || !this.viewport || this.saving || this.blocked) {
      return;
    }
    const point = this.pdfPoint(event, (event.currentTarget as HTMLElement).parentElement);
    field.placement.left = point.left - this.drag.leftOffset;
    field.placement.top = point.top - this.drag.topOffset;
    this.clampField(field);
  }

  public stopDrag(): void {
    this.drag = null;
  }

  public moveWithKeyboard(event: KeyboardEvent, field: SignatureField): void {
    const offsets: {[key: string]: [number, number]} = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]
    };
    if (!offsets[event.key] || this.saving || this.blocked) {
      return;
    }
    event.preventDefault();
    const step = event.shiftKey ? 10 : 1;
    field.placement.left += offsets[event.key][0] * step;
    field.placement.top += offsets[event.key][1] * step;
    this.clampField(field);
  }

  public save(send = false): void {
    if (!this.canSave || (send && !this.fields.length)) {
      return;
    }
    this.saving = true;
    const fields: SignatureInfo[] = this.fields.map((field) => ({
      markerName: field.role,
      signingFieldPlacement: {...field.placement}
    }));
    this.documentsService.defineSignatureFields(this.document.id, fields).pipe(
      tap((document) => {
        this.documentsService.refreshDocumentList$.next(true);
        this.modalService.closeTopmostModal();
        this.reset();
        if (send) {
          this.sendForSignature.emit(document);
        } else {
          this.toastrService.success(this.translate.instant('DOCUMENTS.signature_fields_saved'));
        }
      }),
      catchError((error) => {
        this.blocked = [400, 403, 404].includes(error.status);
        if (this.blocked) {
          this.documentsService.refreshDocumentList$.next(true);
        }
        this.toastrService.error(this.translate.instant(this.blocked
          ? 'DOCUMENTS.signature_fields_unavailable' : 'DOCUMENTS.signature_fields_save_failed'));
        return EMPTY;
      }),
      finalize(() => {
        this.saving = false;
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public reset(): void {
    this.close$.next();
    this.visible = false;
    this.source = null;
    this.roles = [];
    this.fields = [];
    this.pageNumber = 1;
    this.pageCount = 0;
    this.zoom = 0.8;
    this.viewport = null;
    this.loading = false;
    this.loadFailed = false;
    this.blocked = false;
    this.placingRole = null;
    this.pendingDeletionRole = null;
    this.selectedFieldId = null;
    this.drag = null;
    this.roleControl.reset('');
    this.cd.markForCheck();
  }

  private pdfPoint(event: PointerEvent, overlay = event.currentTarget as HTMLElement): {left: number; top: number} {
    const bounds = overlay.getBoundingClientRect();
    const point = this.viewport.convertToPdfPoint(event.clientX - bounds.left, event.clientY - bounds.top);
    return {left: point[0] - this.pageView[0], top: this.pageView[3] - point[1]};
  }

  private clampField(field: SignatureField): void {
    const width = this.pageView[2] - this.pageView[0];
    const height = this.pageView[3] - this.pageView[1];
    field.placement.left = Math.round(Math.max(0, Math.min(width - field.placement.width, field.placement.left)));
    field.placement.top = Math.round(Math.max(0, Math.min(height - field.placement.height, field.placement.top)));
  }
}